import React, { useMemo, useState } from "react";
import { stripHtml } from "@/utils/format";

interface HotelIntroProps {
  intro: string;
}

type Item = { label: string; value: string };

/**
 * 已知标签集合：把后端 `hotel_intro` 中任意位置的 `XXX：YYY` 拆成结构化字段。
 * 注意：不再假设 KV 段必须在文本开头——后端不同酒店会把 KV 段放在开头 / 中间 / 结尾，
 * 所以这里采用"全局扫描"的解析方式（见 parseIntro）。
 * 顺序用于排版（每行 2 个：开业+客房数 → 电话+邮箱 → 地址）。
 */
const KNOWN_LABELS = [
  "开业",
  "装修",
  "客房数",
  "电话",
  "邮箱",
  "邮编",
  "地址",
];

/**
 * 把后端 `hotel_intro` 文本先做"段落化"预处理，再交给 stripHtml：
 * - 块级结束标签（</p>、</div>、</li>、<br>、<br/>、</tr>）替换为换行符 \n
 *   确保 stripHtml 把块级段落分隔开来（否则会变成 "2018装修：2019" 这种粘连串）
 * - 然后再走 stripHtml（去 HTML 标签、合并剩余空白）
 *
 * 设计背景：原 stripHtml 在多个页面（如 hotel card）只是消除 HTML 标签并合并空白，
 * 修改它会影响全站；这里只在酒店介绍处做"段落化"预处理，最小影响范围。
 */
function paragraphize(raw: string): string {
  if (!raw) return "";
  // 把 <br> 任意形式 / 块级闭合标签 替换为 \n
  const withBreaks = raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n");
  // 开头块级标签也补 \n（如 <p>...</p><p>...</p>）
  const withOpens = withBreaks.replace(/<(p|div|li|tr|h[1-6])\b[^>]*>/gi, "\n");
  return stripHtml(withOpens);
}

/**
 * 解析 `hotel_intro`：
 * 1. 把所有形如 `{label}：{value}` 的 KV 段在全球范围内抽取（不限制位置）
 * 2. 从原始文本中剥离抽出的 KV，剩余文本按句号切段
 *
 * 设计背景：之前版本只扫描文本开头的 KV 段，导致 KV 段出现在中段 / 尾段的酒店
 * 会整段塌成"自由描述"。同时兼容全角 `：` 与半角 `:` 两种分隔符。
 *
 * value 边界优先级（从 `valueStart` 起搜索最近的）：
 *   1) 下一个已知标签的位置
 *   2) 紧邻的空白字符
 *   3) 第一个句号 / 文件末尾
 *
 * 边界词防护：要求 labelStart 之前的字符必须是空白或文本起点，
 * 避免 "详细地址" 被错配成 "地址"。
 */
function parseIntro(raw: string): { kvs: Item[]; paragraphs: string[] } {
  const text = paragraphize(raw);
  if (!text) return { kvs: [], paragraphs: [] };

  // 全量扫描：找出所有 KV 段的位置 [labelStart, valueEnd)
  const foundRanges: Array<{
    label: string;
    labelStart: number;
    valueStart: number;
    valueEnd: number;
    value: string;
  }> = [];

  for (const label of KNOWN_LABELS) {
    // 同时兼容全角 `：` 和半角 `:` 两种分隔符
    const sepVariants = [`${label}：`, `${label}:`];
    for (const sep of sepVariants) {
      let searchFrom = 0;
      while (searchFrom < text.length) {
        const labelStart = text.indexOf(sep, searchFrom);
        if (labelStart < 0) break;
        const valueStart = labelStart + sep.length;
        if (valueStart >= text.length) break;

        // 边界词防护：labelStart 之前必须是空白或文本起点，
        // 避免 "详细地址" 被错配成 "地址"。
        const prevChar = labelStart > 0 ? text[labelStart - 1] : "";
        if (prevChar && !/\s/.test(prevChar)) {
          searchFrom = labelStart + 1;
          continue;
        }

        // 找 valueEnd：下一个已知标签 / 下一个空白 / 第一个句号
        const candidates: number[] = [];
        for (const nl of KNOWN_LABELS) {
          if (nl === label) continue;
          const nlIdx = text.indexOf(`${nl}：`, valueStart);
          if (nlIdx > valueStart) candidates.push(nlIdx);
          const nlIdxHalf = text.indexOf(`${nl}:`, valueStart);
          if (nlIdxHalf > valueStart) candidates.push(nlIdxHalf);
        }
        const spaceMatch = /[\s　]/.exec(text.slice(valueStart));
        const spaceIdx = spaceMatch ? valueStart + spaceMatch.index : -1;
        if (spaceIdx > valueStart) candidates.push(spaceIdx);
        // ★ String.prototype.search 的 TS 类型只声明了一个参数，但运行时支持 fromIndex；
        //   为了过 TS 类型检查，改为手动扫描 [。！？]。
        let punctIdx = -1;
        for (let i = valueStart; i < text.length; i++) {
          const ch = text[i];
          if (ch === "。" || ch === "！" || ch === "？") {
            punctIdx = i;
            break;
          }
        }
        if (punctIdx > valueStart) candidates.push(punctIdx);

        const valueEnd =
          candidates.length > 0 ? Math.min(...candidates) : text.length;
        const value = text.slice(valueStart, valueEnd).trim();

        if (value) {
          foundRanges.push({
            label,
            labelStart,
            valueStart,
            valueEnd,
            value,
          });
        }
        searchFrom = valueEnd;
      }
    }
  }

  // 去重：同一个 labelStart 的多次匹配保留第一次
  const seenStarts = new Set<number>();
  const uniqueRanges = foundRanges
    .sort((a, b) => a.labelStart - b.labelStart)
    .filter((r) => {
      if (seenStarts.has(r.labelStart)) return false;
      seenStarts.add(r.labelStart);
      return true;
    });

  const kvs: Item[] = uniqueRanges.map((r) => ({
    label: r.label,
    value: r.value,
  }));

  // 从原始文本剥离所有 KV 段，剩余按句号切段
  const sortedRanges = [...uniqueRanges].sort((a, b) => a.labelStart - b.labelStart);
  const cut: Array<{ start: number; end: number }> = sortedRanges.map((r) => ({
    start: r.labelStart,
    end: r.valueEnd,
  }));

  const restParts: string[] = [];
  let cursor = 0;
  for (const c of cut) {
    if (c.start > cursor) {
      restParts.push(text.slice(cursor, c.start));
    }
    cursor = c.end;
  }
  if (cursor < text.length) {
    restParts.push(text.slice(cursor));
  }
  const rest = restParts.join("");

  const paragraphs = rest
    .split(/(?<=[。！？])\s*/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return { kvs, paragraphs };
}

/** 按固定顺序展示 KV：开业 / 客房数 / 电话 / 邮箱 / 邮编 / 地址 */
const KV_ORDER = ["开业", "客房数", "电话", "邮箱", "邮编", "地址"];

export default function HotelIntro({ intro }: HotelIntroProps) {
  // ★ 必须走 paragraphize 而不是 stripHtml：
  //   块级闭合标签会被替换为 \n 再合并，否则 KV 段会粘连成 "2018装修：2019"。
  const clean = paragraphize(intro);
  const { kvs, paragraphs } = useMemo(() => parseIntro(clean), [clean]);
  const [expanded, setExpanded] = useState(false);

  if (!clean) return null;

  // 按预设顺序重排 KV，未识别的标签放最后
  const orderedKvs = KV_ORDER.map((l) => kvs.find((kv) => kv.label === l)).filter(
    Boolean,
  ) as Item[];
  const otherKvs = kvs.filter((kv) => !KV_ORDER.includes(kv.label));
  const allKvs = [...orderedKvs, ...otherKvs];

  return (
    <div className="bg-white mt-2 px-5 py-4">
      {/* 标题 */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: "#EEF2FF" }}
        >
          <i className="ri-hotel-line" style={{ color: "#1E3A8A", fontSize: 16 }} />
        </div>
        <div className="text-sm font-semibold text-gray-800">酒店介绍</div>
      </div>

      {/* KV 信息块：短字段两两一行，长字段单独成行 */}
      {allKvs.length > 0 && (
        <div
          className="rounded-xl px-3 py-3 mb-4 space-y-2"
          style={{ background: "#F8F9FC", border: "1px solid #EEF2F7" }}
        >
          {chunkRows(allKvs).map((row, rowIdx) => {
            const isFullRow = row.length === 1; // 长字段独占一行，用整宽
            return (
              <div
                key={rowIdx}
                className={isFullRow ? "" : "grid grid-cols-2 gap-x-3"}
              >
                {row.map((kv, i) => {
                  const isTel = /电话|联系方式/.test(kv.label);
                  return (
                    <div
                      key={i}
                      className="flex items-baseline gap-1.5 text-xs min-w-0"
                    >
                      <span className="text-gray-400 flex-shrink-0">
                        {kv.label}
                      </span>
                      <a
                        href={isTel ? `tel:${kv.value}` : undefined}
                        className={`break-all ${isTel ? "font-medium" : "text-gray-700"}`}
                        style={isTel ? { color: "#1E3A8A" } : undefined}
                      >
                        {kv.value}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* 段落正文 */}
      {paragraphs.length > 0 && (
        <div
          className="text-[13px] text-gray-700 leading-7"
          style={{
            display:
              !expanded && paragraphs.join("").length > 120 ? "-webkit-box" : undefined,
            WebkitLineClamp: !expanded ? 3 : undefined,
            WebkitBoxOrient: "vertical",
            overflow: !expanded ? "hidden" : undefined,
          } as React.CSSProperties}
        >
          {paragraphs.map((p, i) => (
            <p key={i} className="mb-2 last:mb-0">
              {p}
            </p>
          ))}
        </div>
      )}

      {paragraphs.join("").length > 120 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1 text-xs font-medium mx-auto"
          style={{ color: "#1E3A8A" }}
        >
          <span>{expanded ? "收起" : "展开全部"}</span>
          <i
            className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
            style={{ fontSize: 14 }}
          />
        </button>
      )}
    </div>
  );
}

/**
 * 把 KV 数组分成多行：
 * - 长字段（>12 字符，或匹配 电话/邮箱/地址 标签）独占一行
 * - 短字段两两一组
 *
 * 实际效果（基于截图数据）：
 *   [开业, 客房数]  → 一行
 *   [电话]          → 独占一行（值太长）
 *   [邮编]          → 独占一行（值太长）
 *   [地址]          → 独占一行
 */
function chunkRows(items: Item[]): Item[][] {
  const LONG_LABELS = ["电话", "邮箱", "邮编", "地址"];
  const isLong = (kv: Item) =>
    LONG_LABELS.includes(kv.label) || kv.value.length > 12;

  const rows: Item[][] = [];
  let buf: Item[] = [];
  for (const kv of items) {
    if (isLong(kv)) {
      // 长字段单独一行：先 flush buffer，再独占一行
      if (buf.length) {
        rows.push(buf);
        buf = [];
      }
      rows.push([kv]);
    } else {
      buf.push(kv);
      if (buf.length === 2) {
        rows.push(buf);
        buf = [];
      }
    }
  }
  if (buf.length) rows.push(buf);
  return rows;
}
